import { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Clock, LogIn, LogOut, Coffee, Play, Delete, LogOut as LogOutIcon, Plane, Loader2, CheckCircle2, XCircle, AlertCircle, User, Download, FileText, ShieldCheck as ShieldIcon, Calendar as CalendarIcon, ChevronLeft, ChevronRight, ShoppingCart, Package as PackageIcon, Trash2 } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "vera_clockin_token";

const fmtTime = (iso) => iso ? new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—";
const fmtHours = (h) => {
  const hh = Math.floor(h); const mm = Math.round((h - hh) * 60);
  return `${hh}h ${String(mm).padStart(2, "0")}m`;
};

const elapsedFor = (iso) => {
  const s = new Date(iso); const ms = Date.now() - s.getTime();
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600); const m = Math.floor((total % 3600) / 60); const sec = total % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
};

function PinPad({ onSubmit, submitting, mode, onModeChange }) {
  const [pin, setPin] = useState("");
  const press = (d) => setPin((p) => (p + d).slice(0, 8));
  const clear = () => setPin("");
  const back = () => setPin((p) => p.slice(0, -1));

  const submit = () => {
    if (pin.length < 4) { toast.error("Enter your 4–8 digit PIN"); return; }
    onSubmit(pin);
    setPin("");
  };

  // Portal mode is styled indigo/pink so the employee can see at a glance that
  // pressing Sign In will drop them into the full profile portal instead of a
  // fast tap-and-go punch.
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#1a1c2e] p-4">
      <Card className="w-full max-w-sm p-6 bg-[#faf6f0] border-none shadow-2xl border-t-4 border-t-indigo-600">
        <ModeToggle mode={mode} onChange={onModeChange} />
        <div className="flex items-center gap-3 mb-4">
          <img src="/logo.png" alt="Vera" className="w-12 h-12 object-contain" />
          <div>
            <div className="font-heading text-2xl font-bold text-indigo-900 leading-none">Vera Portal</div>
            <div className="text-xs text-indigo-700 mt-1">PTO · Calendar · Requests · Profile</div>
          </div>
        </div>
        <h1 className="text-lg font-heading font-semibold text-indigo-900 mb-1">Sign in to your portal</h1>
        <p className="text-xs text-slate-500 mb-4">Manage PTO, calendar, purchase requests and documents.</p>

        <div className="flex justify-center gap-2 mb-6" data-testid="pin-display">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={`dot-${i}`} className={`w-3 h-3 rounded-full ${i < pin.length ? "bg-indigo-600" : "bg-slate-200"}`} />
          ))}
        </div>

        <div className="grid grid-cols-3 gap-2 mb-4">
          {["1","2","3","4","5","6","7","8","9"].map((d) => (
            <Button key={d} data-testid={`pinpad-${d}`} type="button" variant="outline"
              className="h-14 text-xl font-mono border-slate-200 hover:bg-indigo-50 hover:border-indigo-300"
              onClick={() => press(d)}>{d}</Button>
          ))}
          <Button type="button" variant="ghost" className="h-14" onClick={clear} data-testid="pinpad-clear">Clear</Button>
          <Button type="button" variant="outline" className="h-14 text-xl font-mono" onClick={() => press("0")} data-testid="pinpad-0">0</Button>
          <Button type="button" variant="ghost" className="h-14" onClick={back} data-testid="pinpad-back"><Delete className="w-5 h-5" /></Button>
        </div>
        <Button data-testid="pin-submit" onClick={submit} disabled={submitting || pin.length < 4}
          className="w-full h-12 bg-indigo-600 hover:bg-indigo-700 text-lg">
          {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : "Sign in to portal"}
        </Button>
      </Card>
    </div>
  );
}

// Standalone header block used by BOTH pads so the toggle is always in the same
// spot regardless of quick / portal mode. Green ↔ indigo colour cue plus a
// short caption explains the effect of switching.
function ModeToggle({ mode, onChange }) {
  const isQuick = mode === "quick";
  return (
    <div className="flex items-center justify-between gap-2 mb-4 p-2 rounded-lg bg-slate-100">
      <div className="text-[10px] uppercase font-semibold tracking-wider text-slate-500">Mode</div>
      <div role="tablist" className="flex bg-white rounded-md p-0.5 shadow-inner">
        <button data-testid="mode-quick" role="tab" aria-selected={isQuick}
          onClick={() => onChange("quick")}
          className={`px-3 h-7 text-[11px] font-semibold rounded transition-colors ${
            isQuick ? "bg-emerald-600 text-white" : "text-slate-500 hover:text-emerald-700"}`}>
          Quick Punch
        </button>
        <button data-testid="mode-portal" role="tab" aria-selected={!isQuick}
          onClick={() => onChange("portal")}
          className={`px-3 h-7 text-[11px] font-semibold rounded transition-colors ${
            !isQuick ? "bg-indigo-600 text-white" : "text-slate-500 hover:text-indigo-700"}`}>
          Portal Login
        </button>
      </div>
    </div>
  );
}

function QuickPunchPad({ mode, onModeChange }) {
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(null);
  const [flash, setFlash] = useState(null);
  const press = (d) => setPin((p) => (p + d).slice(0, 8));
  const clear = () => setPin("");
  const back = () => setPin((p) => p.slice(0, -1));

  const punch = async (action) => {
    if (pin.length < 4) { toast.error("Enter your 4–8 digit PIN"); return; }
    setBusy(action);
    try {
      const { data } = await axios.post(`${API}/clockin/quick-punch`, { pin, action });
      setFlash({ ok: true, message: `${data.action} — ${data.staff_name}`, at: data.at });
      setPin("");
      setTimeout(() => setFlash(null), 3500);
    } catch (e) {
      setFlash({ ok: false, message: e.response?.data?.detail || "Punch failed" });
      setTimeout(() => setFlash(null), 3500);
    } finally { setBusy(null); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0e2f24] p-4">
      <Card className="w-full max-w-sm p-6 bg-[#f4faf6] border-none shadow-2xl border-t-4 border-t-emerald-600" data-testid="quick-punch-card">
        <ModeToggle mode={mode} onChange={onModeChange} />
        <div className="flex items-center gap-3 mb-4">
          <img src="/logo.png" alt="Vera" className="w-12 h-12 object-contain" />
          <div>
            <div className="font-heading text-2xl font-bold text-emerald-900 leading-none">Quick Punch</div>
            <div className="text-xs text-emerald-800 mt-1">Tap-and-go clock in / out & lunch</div>
          </div>
        </div>

        {flash ? (
          <div data-testid="punch-flash" className={`p-4 rounded-lg mb-4 text-center border ${
            flash.ok ? "bg-emerald-100 border-emerald-300 text-emerald-900"
                     : "bg-rose-100 border-rose-300 text-rose-800"}`}>
            <div className="text-xl font-heading font-bold">{flash.message}</div>
            {flash.at && <div className="text-xs mt-1 font-mono">at {new Date(flash.at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>}
          </div>
        ) : (
          <h1 className="text-sm text-emerald-900 mb-3">Enter your PIN, then tap Clock or Lunch.</h1>
        )}

        <div className="flex justify-center gap-2 mb-5" data-testid="pin-display">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={`qd-${i}`} className={`w-3 h-3 rounded-full ${i < pin.length ? "bg-emerald-600" : "bg-slate-200"}`} />
          ))}
        </div>

        <div className="grid grid-cols-3 gap-2 mb-4">
          {["1","2","3","4","5","6","7","8","9"].map((d) => (
            <Button key={d} data-testid={`pinpad-${d}`} type="button" variant="outline"
              className="h-14 text-xl font-mono border-slate-200 hover:bg-emerald-50 hover:border-emerald-300"
              onClick={() => press(d)}>{d}</Button>
          ))}
          <Button type="button" variant="ghost" className="h-14" onClick={clear} data-testid="pinpad-clear">Clear</Button>
          <Button type="button" variant="outline" className="h-14 text-xl font-mono" onClick={() => press("0")} data-testid="pinpad-0">0</Button>
          <Button type="button" variant="ghost" className="h-14" onClick={back} data-testid="pinpad-back"><Delete className="w-5 h-5" /></Button>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <Button data-testid="quick-clock" onClick={() => punch("clock")} disabled={!!busy || pin.length < 4}
            className="h-14 bg-emerald-600 hover:bg-emerald-700 text-base font-semibold gap-1.5">
            {busy === "clock" ? <Loader2 className="w-5 h-5 animate-spin" /> : <><LogIn className="w-5 h-5" /> Clock In/Out</>}
          </Button>
          <Button data-testid="quick-lunch" onClick={() => punch("lunch")} disabled={!!busy || pin.length < 4}
            variant="outline" className="h-14 border-amber-400 text-amber-800 hover:bg-amber-50 text-base font-semibold gap-1.5">
            {busy === "lunch" ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Coffee className="w-5 h-5" /> Lunch In/Out</>}
          </Button>
        </div>
        <p className="mt-4 text-[11px] text-slate-500 text-center">
          Server figures out whether you're punching in or out based on your last entry.
        </p>
      </Card>
    </div>
  );
}

function LiveTicker({ startIso }) {
  const [t, setT] = useState(elapsedFor(startIso));
  useEffect(() => {
    const iv = setInterval(() => setT(elapsedFor(startIso)), 1000);
    return () => clearInterval(iv);
  }, [startIso]);
  return <span className="font-mono">{t}</span>;
}

function PortalDashboard({ token, onLogout }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const [ptoOpen, setPtoOpen] = useState(false);
  const [now, setNow] = useState(new Date());
  const wallTimer = useRef();

  const reload = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/clockin/me`, { headers: { "X-Staff-Token": token } });
      setData(r.data);
    } catch (e) {
      if (e.response?.status === 401) onLogout();
    }
  }, [token, onLogout]);

  useEffect(() => { reload(); }, [reload]);
  useEffect(() => { wallTimer.current = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(wallTimer.current); }, []);

  // Kiosk auto-lock — sign the portal out after 3 minutes of no user activity so a
  // shared tablet never leaks the next employee's PTO / requests / profile.
  // Reset on any interaction (pointerdown, keydown, scroll, touchstart). 30s
  // before the lock triggers we show a persistent warning toast so the employee
  // can tap to stay signed in.
  useEffect(() => {
    const IDLE_MS = 3 * 60 * 1000;
    const WARN_MS = 2.5 * 60 * 1000;
    let idleTimer, warnTimer;
    let warned = false;
    const kick = () => {
      warned = false;
      toast.dismiss("kiosk-idle");
      clearTimeout(warnTimer); clearTimeout(idleTimer);
      warnTimer = setTimeout(() => {
        warned = true;
        toast.warning("Signing out for privacy in 30s — tap anywhere to stay in.", {
          id: "kiosk-idle", duration: 30_000,
        });
      }, WARN_MS);
      idleTimer = setTimeout(() => {
        toast.dismiss("kiosk-idle");
        toast.info("Signed out — kiosk auto-lock (3 min idle).", { id: "kiosk-lock" });
        onLogout();
      }, IDLE_MS);
    };
    const events = ["pointerdown", "keydown", "scroll", "touchstart"];
    events.forEach((ev) => window.addEventListener(ev, kick, { passive: true }));
    kick();
    return () => {
      events.forEach((ev) => window.removeEventListener(ev, kick));
      clearTimeout(warnTimer); clearTimeout(idleTimer);
      toast.dismiss("kiosk-idle");
    };
  }, [onLogout]);

  const punch = async (event_type, action) => {
    setBusy(`${event_type}-${action}`);
    try {
      await axios.post(`${API}/clockin/punch`, { event_type, action }, { headers: { "X-Staff-Token": token } });
      const verb = action === "in" ? "clocked in" : "clocked out";
      toast.success(`${event_type === "shift" ? "Shift" : "Lunch"} ${verb}`);
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Punch failed");
    } finally { setBusy(null); }
  };

  if (!data) return <div className="min-h-screen flex items-center justify-center bg-[#faf6f0]"><Loader2 className="w-8 h-8 animate-spin text-pink-700" /></div>;

  const { staff, open_shift, open_lunch, week, pto_balance, pto_requests } = data;

  return (
    <div className="min-h-screen bg-[#faf6f0]">
      <header className="bg-[#3b2418] text-[#f7d8c8] px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src="/logo.png" alt="Vera" className="w-9 h-9 object-contain bg-[#faf6f0] rounded p-0.5" />
          <div>
            <div className="font-heading text-lg font-bold text-white leading-none">Hi, {staff.name.split(" ")[0]}</div>
            <div className="text-[11px] text-[#f7d8c8]/80">{staff.department || "Time Clock"}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="text-right text-xs font-mono hidden sm:block">
            <div className="text-[#f7d8c8]/70">{now.toLocaleDateString([], { weekday: "long", month: "short", day: "numeric" })}</div>
            <div className="text-white font-semibold">{now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}</div>
          </div>
          <Button data-testid="clockin-logout" onClick={onLogout} variant="ghost" size="sm" className="text-[#f7d8c8] hover:bg-[#4a2f22]"><LogOutIcon className="w-4 h-4" /></Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-4 md:p-6 space-y-6">
        <Tabs defaultValue="clock" className="w-full">
          <TabsList className="h-10 bg-white border border-slate-200 p-1 flex flex-wrap">
            <TabsTrigger data-testid="ci-tab-clock" value="clock" className="gap-1.5"><Clock className="w-4 h-4" /> Clock</TabsTrigger>
            <TabsTrigger data-testid="ci-tab-calendar" value="calendar" className="gap-1.5"><CalendarIcon className="w-4 h-4" /> Calendar</TabsTrigger>
            <TabsTrigger data-testid="ci-tab-requests" value="requests" className="gap-1.5"><ShoppingCart className="w-4 h-4" /> Requests</TabsTrigger>
            <TabsTrigger data-testid="ci-tab-profile" value="profile" className="gap-1.5"><User className="w-4 h-4" /> My Profile</TabsTrigger>
            <TabsTrigger data-testid="ci-tab-downloads" value="downloads" className="gap-1.5"><Download className="w-4 h-4" /> Documents</TabsTrigger>
          </TabsList>

          <TabsContent value="clock" className="mt-4 space-y-6">
        {/* Big status card (comes first — user asked for the clock action on top, calendar below) */}
        <Card className="p-6 border-slate-200" data-testid="clockin-status-card">
          {open_shift ? (
            <div className="text-center">
              <div className="inline-flex items-center gap-1.5 bg-emerald-100 text-emerald-800 px-3 py-1 rounded-full text-xs font-semibold mb-4">
                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                {open_lunch ? "On Lunch" : "Clocked In"}
              </div>
              <div className="text-slate-500 text-sm mb-2">Shift started at {fmtTime(open_shift.clock_in)}</div>
              <div className="text-6xl font-heading font-bold text-pink-800 tracking-tight mb-1" data-testid="live-ticker">
                <LiveTicker startIso={open_lunch ? open_lunch.clock_in : open_shift.clock_in} />
              </div>
              <div className="text-xs text-slate-400 mb-6">{open_lunch ? "Lunch elapsed" : "Shift elapsed"}</div>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                {open_lunch ? (
                  <Button data-testid="lunch-end-btn" onClick={() => punch("lunch", "out")} disabled={busy}
                    className="bg-amber-500 hover:bg-amber-600 h-14 text-base flex-1 gap-2">
                    <Play className="w-5 h-5" /> End Lunch
                  </Button>
                ) : (
                  <Button data-testid="lunch-start-btn" onClick={() => punch("lunch", "in")} disabled={busy}
                    variant="outline" className="h-14 text-base flex-1 gap-2 border-amber-300 text-amber-700 hover:bg-amber-50">
                    <Coffee className="w-5 h-5" /> Start Lunch
                  </Button>
                )}
                <Button data-testid="clock-out-btn" onClick={() => punch("shift", "out")} disabled={busy}
                  className="bg-[#3b2418] hover:bg-[#4a2f22] h-14 text-base flex-1 gap-2">
                  <LogOut className="w-5 h-5" /> Clock Out
                </Button>
              </div>
            </div>
          ) : (
            <div className="text-center">
              <div className="text-slate-500 text-sm mb-2">You are currently</div>
              <div className="text-3xl font-heading font-bold text-slate-800 mb-6">Clocked Out</div>
              <Button data-testid="clock-in-btn" onClick={() => punch("shift", "in")} disabled={busy}
                className="bg-pink-700 hover:bg-pink-800 h-14 px-10 text-base gap-2">
                <LogIn className="w-5 h-5" /> Clock In
              </Button>
            </div>
          )}
        </Card>

        {/* This week */}
        <Card className="p-5 border-slate-200">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-pink-700" />
              <h3 className="font-heading font-semibold text-slate-800">This Week</h3>
              <span className="text-xs text-slate-400 font-mono">{week.start} → {week.end}</span>
            </div>
            <div className="text-right">
              <div className="text-xs text-slate-500">Net hours</div>
              <div className="font-mono font-bold text-pink-800 text-lg" data-testid="week-net-hours">{fmtHours(week.totals.net)}</div>
            </div>
          </div>
          {week.entries.length === 0 ? (
            <p className="text-sm text-slate-400 py-4 text-center">No shifts this week yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="week-entries-table">
                <thead className="text-xs text-slate-500 border-b border-slate-100">
                  <tr><th className="text-left py-2">Date</th><th className="text-left">Type</th><th className="text-left">In</th><th className="text-left">Out</th><th className="text-right">Hours</th></tr>
                </thead>
                <tbody>
                  {week.entries.map((e) => (
                    <tr key={e.id} className="border-b border-slate-50">
                      <td className="py-2 font-mono text-xs">{e.date}</td>
                      <td><span className={`text-xs px-1.5 py-0.5 rounded ${e.event_type === "shift" ? "bg-pink-50 text-pink-800" : "bg-amber-50 text-amber-700"}`}>{e.event_type}</span></td>
                      <td className="font-mono text-xs">{fmtTime(e.clock_in)}</td>
                      <td className="font-mono text-xs">{e.clock_out ? fmtTime(e.clock_out) : <span className="text-emerald-600">Active</span>}</td>
                      <td className="text-right font-mono">{e.clock_out ? fmtHours((new Date(e.clock_out) - new Date(e.clock_in)) / 3600000) : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* PTO */}
        <Card className="p-5 border-slate-200" data-testid="pto-card">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Plane className="w-4 h-4 text-pink-700" />
              <h3 className="font-heading font-semibold text-slate-800">Paid Time Off</h3>
            </div>
            <Button data-testid="pto-request-btn" onClick={() => setPtoOpen(true)} className="bg-pink-700 hover:bg-pink-800 h-9 gap-1.5">
              <Plane className="w-4 h-4" /> Request PTO
            </Button>
          </div>
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div className="text-center p-3 bg-slate-50 rounded-lg">
              <div className="text-xs text-slate-500">Total</div>
              <div className="font-heading text-xl font-bold text-slate-800 font-mono">{pto_balance.total}h</div>
            </div>
            <div className="text-center p-3 bg-amber-50 rounded-lg">
              <div className="text-xs text-slate-500">Used</div>
              <div className="font-heading text-xl font-bold text-amber-700 font-mono">{pto_balance.used}h</div>
            </div>
            <div className="text-center p-3 bg-emerald-50 rounded-lg">
              <div className="text-xs text-slate-500">Available</div>
              <div className="font-heading text-xl font-bold text-emerald-700 font-mono" data-testid="pto-available">{pto_balance.available}h</div>
            </div>
          </div>
          {pto_requests.length === 0 ? (
            <p className="text-sm text-slate-400 py-2 text-center">No PTO requests yet.</p>
          ) : (
            <div className="space-y-2">
              {pto_requests.slice(0, 5).map((r) => (
                <div key={r.id} className="flex items-center justify-between p-2.5 border border-slate-100 rounded-lg text-sm">
                  <div>
                    <div className="font-medium text-slate-800">{r.start_date}{r.end_date !== r.start_date ? ` → ${r.end_date}` : ""} · {r.hours}h</div>
                    <div className="text-xs text-slate-400">{r.reason || "No reason"}{r.admin_reason ? ` · Manager: ${r.admin_reason}` : ""}</div>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-semibold inline-flex items-center gap-1
                    ${r.status === "approved" ? "bg-emerald-100 text-emerald-800" : r.status === "denied" ? "bg-rose-100 text-rose-800" : "bg-amber-100 text-amber-800"}`}>
                    {r.status === "approved" ? <CheckCircle2 className="w-3 h-3" /> : r.status === "denied" ? <XCircle className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
                    {r.status}
                  </span>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Company calendar — placed below the clock action per user request */}
        <CalendarTab token={token} />
          </TabsContent>
          <TabsContent value="calendar" className="mt-4"><CalendarTab token={token} /></TabsContent>
          <TabsContent value="requests" className="mt-4"><PurchaseRequestsTab token={token} /></TabsContent>
          <TabsContent value="profile" className="mt-4"><ProfileTab token={token} /></TabsContent>
          <TabsContent value="downloads" className="mt-4"><DownloadsTab token={token} /></TabsContent>
        </Tabs>
      </main>

      <PtoRequestDialog open={ptoOpen} onClose={() => setPtoOpen(false)} token={token} onSubmitted={reload} available={pto_balance.available} />
    </div>
  );
}

/* ---------------- Shared calendar (approved PTO + admin events) ---------------- */
function CalendarTab({ token }) {
  const [monthStart, setMonthStart] = useState(() => {
    const d = new Date(); d.setDate(1); d.setHours(0, 0, 0, 0); return d;
  });
  const [data, setData] = useState({ events: [], pto: [] });
  const [selectedDay, setSelectedDay] = useState(null);

  const start = ymd(monthStart);
  const endDate = new Date(monthStart.getFullYear(), monthStart.getMonth() + 1, 0);
  const end = ymd(endDate);

  useEffect(() => {
    axios.get(`${API}/clockin/calendar?start=${start}&end=${end}`,
      { headers: { "X-Staff-Token": token } })
      .then((r) => setData(r.data || { events: [], pto: [] }))
      .catch(() => setData({ events: [], pto: [] }));
  }, [start, end, token]);

  const shift = (n) => setMonthStart((d) => {
    const nd = new Date(d); nd.setMonth(nd.getMonth() + n); return nd;
  });

  const items = [...(data.events || []), ...(data.pto || [])];
  const byDay = {};
  const inRange = (d, s, e) => d >= s && d <= e;
  const weekdayShift = new Date(monthStart).getDay();

  const days = [];
  for (let i = 0; i < weekdayShift; i++) days.push(null);
  const lastDay = endDate.getDate();
  for (let d = 1; d <= lastDay; d++) {
    const iso = ymd(new Date(monthStart.getFullYear(), monthStart.getMonth(), d));
    byDay[iso] = items.filter((it) => inRange(iso, it.start_date, it.end_date));
    days.push({ d, iso });
  }
  while (days.length % 7 !== 0) days.push(null);

  const monthLabel = monthStart.toLocaleDateString([], { month: "long", year: "numeric" });
  const selectedItems = selectedDay ? (byDay[selectedDay] || []) : [];

  return (
    <>
      <Card className="p-4 border-slate-200" data-testid="clockin-calendar">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-xs uppercase text-slate-500 tracking-wide">Company calendar</div>
            <div className="text-lg font-heading font-bold text-slate-800">{monthLabel}</div>
          </div>
          <div className="flex gap-1.5">
            <Button data-testid="cal-prev" size="sm" variant="outline" onClick={() => shift(-1)}
              className="h-8 w-8 p-0"><ChevronLeft className="w-4 h-4" /></Button>
            <Button data-testid="cal-today" size="sm" variant="outline" onClick={() => setMonthStart(() => {
              const d = new Date(); d.setDate(1); d.setHours(0, 0, 0, 0); return d;
            })} className="h-8 text-xs">Today</Button>
            <Button data-testid="cal-next" size="sm" variant="outline" onClick={() => shift(1)}
              className="h-8 w-8 p-0"><ChevronRight className="w-4 h-4" /></Button>
          </div>
        </div>
        <div className="grid grid-cols-7 gap-px bg-slate-200 text-[10px] text-slate-500 font-semibold uppercase">
          {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map((d) => (
            <div key={d} className="bg-slate-50 py-1.5 text-center">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-px bg-slate-200">
          {days.map((day, i) => {
            if (!day) return <div key={i} className="bg-slate-50 min-h-[70px]"></div>;
            const evs = byDay[day.iso] || [];
            const active = selectedDay === day.iso;
            return (
              <button key={i} onClick={() => setSelectedDay(active ? null : day.iso)}
                data-testid={`cal-day-${day.iso}`}
                className={`bg-white min-h-[70px] p-1.5 text-left hover:bg-amber-50 ${active ? "ring-2 ring-pink-700 ring-inset" : ""}`}>
                <div className="text-[11px] font-semibold text-slate-700">{day.d}</div>
                <div className="mt-1 space-y-0.5">
                  {evs.slice(0, 3).map((e, k) => (
                    <div key={k} className="text-[9px] truncate px-1 py-0.5 rounded"
                      style={{ backgroundColor: e.color + "22", color: e.color, borderLeft: `2px solid ${e.color}` }}>
                      {e.title}
                    </div>
                  ))}
                  {evs.length > 3 && (
                    <div className="text-[9px] text-slate-400">+{evs.length - 3} more</div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
        <div className="mt-3 flex gap-3 text-[11px] text-slate-500">
          <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-amber-500" /> Approved PTO</span>
          <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-teal-600" /> Company event</span>
        </div>
      </Card>

      {selectedDay && (
        <Card className="p-4 border-slate-200 mt-3" data-testid="cal-day-details">
          <div className="text-xs uppercase text-slate-500 mb-1">On {selectedDay}</div>
          {selectedItems.length === 0 ? (
            <div className="text-sm text-slate-400 italic">Nothing scheduled.</div>
          ) : (
            <ul className="space-y-1.5">
              {selectedItems.map((it) => (
                <li key={it.id} className="text-sm flex items-start gap-2">
                  <span className="w-2.5 h-2.5 rounded-sm mt-1.5 shrink-0"
                    style={{ backgroundColor: it.color }} />
                  <div>
                    <div className="font-medium text-slate-800">{it.title}</div>
                    {it.note && <div className="text-xs text-slate-500">{it.note}</div>}
                    {it.hours > 0 && <div className="text-[11px] text-amber-700">{it.hours}h PTO</div>}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      )}
    </>
  );
}

const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;


function ProfileTab({ token }) {
  const [p, setP] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    axios.get(`${API}/clockin/profile`, { headers: { "X-Staff-Token": token } })
      .then((r) => setP(r.data)).catch(() => {});
  }, [token]);
  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/clockin/profile`, p, { headers: { "X-Staff-Token": token } });
      toast.success("Profile updated");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  if (!p) return <Card className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-pink-700" /></Card>;
  const readonly = [["Name", p.name], ["Title", p.title], ["Department", p.department],
    ["Hire Date", p.hire_date], ["License #", p.license_number], ["License Expiry", p.license_expiry]];
  const editable = [
    ["email", "Email"], ["phone", "Phone"], ["address", "Home Address"],
    ["allergies", "Allergies"], ["emergency_contact_name", "Emergency Contact Name"],
    ["emergency_contact_phone", "Emergency Contact Phone"], ["emergency_contact_relationship", "Relationship"],
  ];
  return (
    <Card className="p-5 border-slate-200 space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {readonly.map(([lbl, v]) => (
          <div key={lbl}><Label className="text-xs text-slate-500">{lbl}</Label>
            <div className="text-sm font-medium text-slate-800 mt-1">{v || "—"}</div></div>
        ))}
      </div>
      <div className="border-t pt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
        {editable.map(([k, lbl]) => (
          <div key={k}><Label className="text-xs text-slate-600">{lbl}</Label>
            <Input data-testid={`profile-${k}`} value={p[k] || ""} onChange={(e) => setP({ ...p, [k]: e.target.value })} className="mt-1" /></div>
        ))}
      </div>
      <div className="flex justify-end"><Button data-testid="profile-save" onClick={save} disabled={saving} className="bg-pink-700 hover:bg-pink-800 gap-1.5">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <User className="w-4 h-4" />} Save My Profile</Button></div>
    </Card>
  );
}

const PR_CATEGORIES = ["Office Supplies", "Pharmacy Supplies", "Compounding Materials",
                        "Cleaning & Sanitation", "IT / Technology", "Equipment / Maintenance", "Other"];
const PR_URGENCIES = ["Low", "Normal", "High", "Urgent"];

const PR_STATUS_STYLES = {
  pending:  { chip: "bg-amber-100 text-amber-800",     icon: <AlertCircle className="w-3 h-3" /> },
  approved: { chip: "bg-emerald-100 text-emerald-800", icon: <CheckCircle2 className="w-3 h-3" /> },
  ordered:  { chip: "bg-sky-100 text-sky-800",         icon: <PackageIcon className="w-3 h-3" /> },
  received: { chip: "bg-slate-200 text-slate-700",     icon: <CheckCircle2 className="w-3 h-3" /> },
  denied:   { chip: "bg-rose-100 text-rose-800",       icon: <XCircle className="w-3 h-3" /> },
};

function PurchaseRequestsTab({ token }) {
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const empty = { item: "", quantity: "1", category: "Office Supplies",
                  urgency: "Normal", estimated_cost: "", vendor_hint: "", notes: "" };
  const [form, setForm] = useState(empty);

  const load = useCallback(() => {
    axios.get(`${API}/clockin/purchase-requests`, { headers: { "X-Staff-Token": token } })
      .then((r) => setRows(r.data || [])).catch(() => setRows([]));
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!form.item.trim()) { toast.error("Item is required"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/clockin/purchase-requests`, form,
        { headers: { "X-Staff-Token": token } });
      toast.success("Request sent to admin");
      setOpen(false); setForm(empty); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Submit failed"); }
    finally { setSaving(false); }
  };

  const cancel = async (id) => {
    if (!window.confirm("Cancel this pending request?")) return;
    try {
      await axios.delete(`${API}/clockin/purchase-requests/${id}`,
        { headers: { "X-Staff-Token": token } });
      toast.success("Canceled"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Cancel failed"); }
  };

  if (rows === null) {
    return <Card className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-pink-700" /></Card>;
  }

  return (
    <>
      <Card className="p-5 border-slate-200" data-testid="requests-card">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <ShoppingCart className="w-4 h-4 text-pink-700" />
            <h3 className="font-heading font-semibold text-slate-800">Purchase Requests</h3>
          </div>
          <Button data-testid="request-new-btn" onClick={() => setOpen(true)}
            className="bg-pink-700 hover:bg-pink-800 h-9 gap-1.5">
            <ShoppingCart className="w-4 h-4" /> New request
          </Button>
        </div>
        <p className="text-xs text-slate-500 mb-3">
          Need office supplies, replacement pharmacy items, cleaning supplies, or IT gear? Send admin a
          request from here — they'll approve, order, and mark received.
        </p>
        {rows.length === 0 ? (
          <p className="text-sm text-slate-400 py-6 text-center">No requests yet.</p>
        ) : (
          <div className="space-y-2">
            {rows.map((r) => {
              const style = PR_STATUS_STYLES[r.status] || PR_STATUS_STYLES.pending;
              return (
                <div key={r.id} data-testid={`request-row-${r.id}`}
                  className="p-3 border border-slate-100 rounded-lg text-sm">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-medium text-slate-800 truncate">{r.item}
                        <span className="ml-2 text-xs font-mono text-slate-400">×{r.quantity}</span>
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5">
                        {r.category} · {r.urgency}
                        {r.estimated_cost > 0 && <> · ~${r.estimated_cost.toFixed(2)}</>}
                        {r.vendor_hint && <> · {r.vendor_hint}</>}
                      </div>
                      {r.notes && <div className="text-xs text-slate-400 mt-1 italic">"{r.notes}"</div>}
                      {r.admin_note && (
                        <div className="text-xs text-slate-700 mt-1.5 p-2 bg-slate-50 rounded">
                          <span className="font-semibold">Admin:</span> {r.admin_note}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-1 shrink-0">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-semibold inline-flex items-center gap-1 ${style.chip}`}>
                        {style.icon}{r.status}
                      </span>
                      {r.status === "pending" && (
                        <Button data-testid={`request-cancel-${r.id}`} size="sm" variant="ghost"
                          className="h-7 text-xs text-slate-400 hover:text-rose-600"
                          onClick={() => cancel(r.id)}>
                          <Trash2 className="w-3 h-3 mr-1" /> Cancel
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      <Dialog open={open} onOpenChange={(o) => !o && setOpen(false)}>
        <DialogContent data-testid="request-dialog">
          <DialogHeader><DialogTitle className="font-heading">New Purchase Request</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">What do you need? *</Label>
              <Input data-testid="req-item" value={form.item}
                onChange={(e) => setForm({ ...form, item: e.target.value })}
                placeholder="e.g. Sterile 10mL vials, box of gloves size M" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Quantity</Label>
                <Input data-testid="req-qty" type="number" min="1" step="1" value={form.quantity}
                  onChange={(e) => setForm({ ...form, quantity: e.target.value })} /></div>
              <div><Label className="text-xs">Category</Label>
                <select data-testid="req-category" value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className="w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm">
                  {PR_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Urgency</Label>
                <select data-testid="req-urgency" value={form.urgency}
                  onChange={(e) => setForm({ ...form, urgency: e.target.value })}
                  className="w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm">
                  {PR_URGENCIES.map((u) => <option key={u} value={u}>{u}</option>)}
                </select></div>
              <div><Label className="text-xs">Est. cost (USD)</Label>
                <Input data-testid="req-cost" type="number" min="0" step="0.01" value={form.estimated_cost}
                  onChange={(e) => setForm({ ...form, estimated_cost: e.target.value })}
                  placeholder="optional" /></div>
            </div>
            <div><Label className="text-xs">Vendor hint (optional)</Label>
              <Input data-testid="req-vendor" value={form.vendor_hint}
                onChange={(e) => setForm({ ...form, vendor_hint: e.target.value })}
                placeholder="e.g. McKesson, Amazon Business" /></div>
            <div><Label className="text-xs">Notes for admin (optional)</Label>
              <Textarea data-testid="req-notes" rows={2} value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Why we need it, deadline, etc." /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="req-submit" onClick={submit} disabled={saving}
              className="bg-pink-700 hover:bg-pink-800">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send to admin"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function DownloadsTab({ token }) {
  const [docs, setDocs] = useState(null);
  useEffect(() => {
    axios.get(`${API}/clockin/downloads`, { headers: { "X-Staff-Token": token } })
      .then((r) => setDocs(r.data.downloads)).catch(() => setDocs([]));
  }, [token]);
  const download = async (doc) => {
    try {
      const backend = process.env.REACT_APP_BACKEND_URL;
      if (doc.kind === "handbook") {
        const res = await axios.get(`${backend}/api/clockin/downloads/handbook.pdf`,
          { headers: { "X-Staff-Token": token }, responseType: "blob" });
        const url = URL.createObjectURL(res.data);
        const a = document.createElement("a"); a.href = url; a.download = "vera-employee-handbook.pdf";
        document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      } else {
        window.open(`${backend}${doc.url}`, "_blank");
      }
    } catch (e) { toast.error("Download failed"); }
  };
  if (!docs) return <Card className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-pink-700" /></Card>;
  return (
    <Card className="p-5 border-slate-200">
      {docs.length === 0 ? (
        <p className="text-sm text-slate-400 text-center py-6">No documents published yet. Ask a manager to add them in the Pharmacy Profile.</p>
      ) : (
        <div className="space-y-3">
          {docs.map((d) => (
            <div key={d.kind} data-testid={`doc-${d.kind}`} className="flex items-center justify-between p-4 border border-slate-100 rounded-lg hover:border-pink-200 transition-colors">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${d.kind === "handbook" ? "bg-pink-50 text-pink-700" : "bg-emerald-50 text-emerald-700"}`}>
                  {d.kind === "handbook" ? <FileText className="w-5 h-5" /> : <ShieldIcon className="w-5 h-5" />}
                </div>
                <div><div className="font-medium text-slate-800">{d.name}</div><div className="text-xs text-slate-400">Tap to download</div></div>
              </div>
              <Button data-testid={`download-${d.kind}`} onClick={() => download(d)} size="sm" className="bg-pink-700 hover:bg-pink-800 gap-1.5"><Download className="w-4 h-4" /> Download</Button>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function PtoRequestDialog({ open, onClose, token, onSubmitted, available }) {
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [hours, setHours] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) { setStart(""); setEnd(""); setHours(""); setReason(""); } }, [open]);

  const submit = async () => {
    if (!start || !hours) { toast.error("Start date and hours required"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/clockin/pto`, {
        start_date: start, end_date: end || start, hours: parseFloat(hours), reason,
      }, { headers: { "X-Staff-Token": token } });
      toast.success("PTO request submitted");
      onClose(); onSubmitted();
    } catch (e) { toast.error(e.response?.data?.detail || "Submit failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="pto-request-dialog">
        <DialogHeader><DialogTitle className="font-heading">Request Paid Time Off</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-xs text-slate-500">You have <strong className="text-emerald-700 font-mono">{available}h</strong> available.</p>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Start date</Label><Input data-testid="pto-start" type="date" value={start} onChange={(e) => setStart(e.target.value)} /></div>
            <div><Label className="text-xs">End date</Label><Input data-testid="pto-end" type="date" value={end} onChange={(e) => setEnd(e.target.value)} /></div>
          </div>
          <div><Label className="text-xs">Total hours</Label><Input data-testid="pto-hours" type="number" step="0.5" value={hours} onChange={(e) => setHours(e.target.value)} placeholder="e.g. 8" /></div>
          <div><Label className="text-xs">Reason (optional)</Label><Textarea data-testid="pto-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={2} placeholder="Family trip, appointment…" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="pto-submit" onClick={submit} disabled={saving} className="bg-pink-700 hover:bg-pink-800">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit request"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function ClockIn() {
  const [token, setToken] = useState(() => localStorage.getItem(STORAGE_KEY));
  const [submitting, setSubmitting] = useState(false);
  // Default = quick per user spec. Persisted so the choice sticks between
  // punches on the shared kiosk tablet.
  const [mode, setMode] = useState(() => localStorage.getItem("vera_clockin_mode") || "quick");
  const changeMode = (m) => { setMode(m); localStorage.setItem("vera_clockin_mode", m); };

  const verify = async (pin) => {
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/clockin/verify-pin`, { pin });
      localStorage.setItem(STORAGE_KEY, r.data.token);
      setToken(r.data.token);
    } catch (e) { toast.error(e.response?.data?.detail || "PIN not recognized"); }
    finally { setSubmitting(false); }
  };

  const logout = async () => {
    try { await axios.post(`${API}/clockin/logout`, {}, { headers: { "X-Staff-Token": token } }); }
    catch (err) { console.warn("clockin logout failed:", err?.message || err); }
    localStorage.removeItem(STORAGE_KEY);
    setToken(null);
  };

  if (token) return <PortalDashboard token={token} onLogout={logout} />;
  return mode === "quick"
    ? <QuickPunchPad mode={mode} onModeChange={changeMode} />
    : <PinPad mode={mode} onModeChange={changeMode} onSubmit={verify} submitting={submitting} />;
}
